import React from "react";
import { DndContext, closestCenter, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import { HotInfoListProps, SortableItemHotInfoProps } from "../../types/screensTypes";
import { deleteHotInfo } from "../../api";
import WaypointCard from "../cards/WaypointCard";

const SortableItem: React.FC<SortableItemHotInfoProps> = ({ id, hotInfo, onClick, onDelete }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        marginBottom: "10px", // Add spacing between items
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <WaypointCard
                name={hotInfo.internalName || t("partner")}
                position={hotInfo.position}
                onClick={onClick}
                onDelete={onDelete}
                dragHandleProps={listeners}
            />
        </div>
    );
};

const HotInfoList: React.FC<HotInfoListProps> = ({
    hotinfos,
    setHotInfos,
    saveHotInfos,
    refetchHotInfo,
    navigateToNext,
}) => {
    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = hotinfos.findIndex((hotinfo) => hotinfo.id === active.id);
        const newIndex = hotinfos.findIndex((hotinfo) => hotinfo.id === over.id);

        // Update the hotInfos array by moving the dragged item to its new position
        const reorderedHotInfos = arrayMove(hotinfos, oldIndex, newIndex);
        setHotInfos(reorderedHotInfos);

        // Save new positions if they have changed
        for (let i = 0; i < reorderedHotInfos.length; i++) {
            const hotinfo = reorderedHotInfos[i];
            const newPosition = i + 1;
            if (hotinfo.position !== newPosition) {
                await saveHotInfos(hotinfo.id, newPosition);
            }
        }

        await refetchHotInfo(); // Refetch to ensure data consistency
    };

    const deleteHotInfoAPI = async (id: string): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token) return;
        await deleteHotInfo({ token, hotInfoId: id });
        await refetchHotInfo();
    };

    return (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={hotinfos.map((wpt) => wpt.id)} strategy={verticalListSortingStrategy}>
                <div className="waypoints-list">
                    {hotinfos.map((hotInfo) => (
                        <SortableItem
                            key={hotInfo.id}
                            id={hotInfo.id}
                            hotInfo={hotInfo}
                            onClick={() => navigateToNext(hotInfo.id)}
                            onDelete={async () => {
                                await deleteHotInfoAPI(hotInfo.id);
                            }}
                        />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
};

export default HotInfoList;
